import React, { useRef, useState } from 'react';
import {
  Image, Modal, Pressable, useWindowDimensions,
} from 'react-native';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
  Center,
  CircleIcon,
  Heading,
  HStack,
  Icon,
  Modal as GluestackModal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  RadioIcon,
  RadioIndicator,
  RadioLabel,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from '@gluestack-ui/themed';
import { Maximize2, RefreshCw, X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { OralExamIdRejectionReason } from '@/common/api/models/oralExamIdRejectionReason';

export const REJECTION_REASON_CODES: OralExamIdRejectionReason[] = [
  OralExamIdRejectionReason.blurry,
  OralExamIdRejectionReason.name_not_legible,
  OralExamIdRejectionReason.face_not_visible,
  OralExamIdRejectionReason.wrong_document,
  OralExamIdRejectionReason.other,
];

const REJECTION_REASON_LEFT: OralExamIdRejectionReason[] = [
  OralExamIdRejectionReason.blurry,
  OralExamIdRejectionReason.face_not_visible,
  OralExamIdRejectionReason.other,
];

const REJECTION_REASON_RIGHT: OralExamIdRejectionReason[] = [
  OralExamIdRejectionReason.name_not_legible,
  OralExamIdRejectionReason.wrong_document,
];

const DIALOG_PORTRAIT_SIZE = 120;
const DIALOG_DOCUMENT_WIDTH = 184;
const DIALOG_DOCUMENT_HEIGHT = 120;

type RetakeDialogProps = {
  isOpen: boolean;
  bookingId: string;
  studentName: string;
  selfieUri: string | null;
  documentUri: string | null;
  selectedCode?: OralExamIdRejectionReason;
  errorMessage?: string;
  pending: boolean;
  onClose: () => void;
  onSelectReason: (code: OralExamIdRejectionReason) => void;
  onSubmit: (note: string) => void;
};

function EnlargeBadgeVisual() {
  return (
    <Box pointerEvents="none" position="absolute" top={6} right={6}>
      <Box w={26} h={26} borderRadius="$full" overflow="hidden" position="relative">
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bg="$bgDark"
          opacity={0.6}
        />
        <Center w={26} h={26}>
          <Icon as={Maximize2} size="2xs" color="$textInverted" />
        </Center>
      </Box>
    </Box>
  );
}

type DialogPhotoProps = {
  uri: string | null;
  width: number;
  height: number;
  caption: string;
  pressTestId: string;
  uriTestId: string;
  zoomTestId: string;
  zoomAccessibilityLabel: string;
  imageAccessibilityLabel: string;
};

function DialogPhoto({
  uri,
  width,
  height,
  caption,
  pressTestId,
  uriTestId,
  zoomTestId,
  zoomAccessibilityLabel,
  imageAccessibilityLabel,
}: DialogPhotoProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const { width: screenW, height: screenH } = useWindowDimensions();
  const lastUriRef = useRef<string | null>(null);
  if (uri) {
    lastUriRef.current = uri;
  }
  const displayUri = uri ?? (isZoomed ? lastUriRef.current : null);

  if (!displayUri && !isZoomed) {
    return null;
  }

  const zoomSize = Math.min(screenW * 0.9, screenH * 0.9, 720);
  const openZoom = () => setIsZoomed(true);

  return (
    <Box width={width} flexShrink={0}>
      <Text size="xs" color="$textSubtle" mb="$1">
        {caption}
      </Text>
      {displayUri ? (
        <Pressable
          onPress={openZoom}
          accessibilityRole="button"
          accessibilityLabel={zoomAccessibilityLabel}
          testID={pressTestId}
        >
          <Box
            w={width}
            h={height}
            borderRadius="$lg"
            overflow="hidden"
            bg="$bgSubtle"
            position="relative"
          >
            <Image
              source={{ uri: displayUri }}
              style={{ width, height }}
              resizeMode="cover"
              accessibilityLabel={imageAccessibilityLabel}
              testID={uriTestId}
            />
            <EnlargeBadgeVisual />
          </Box>
        </Pressable>
      ) : null}

      <Modal
        visible={isZoomed}
        transparent
        animationType="fade"
        onRequestClose={() => setIsZoomed(false)}
      >
        <Pressable
          onPress={() => setIsZoomed(false)}
          accessibilityRole="button"
          accessibilityLabel={`Close ${caption.toLowerCase()}`}
          testID={zoomTestId}
          style={{ flex: 1 }}
        >
          <Box flex={1} bg="rgba(0,0,0,0.85)" alignItems="center" justifyContent="center" p="$6">
            {displayUri ? (
              <Image
                source={{ uri: displayUri }}
                style={{ width: zoomSize, height: zoomSize, borderRadius: 12 }}
                resizeMode="contain"
                accessibilityLabel={`${imageAccessibilityLabel}, enlarged`}
              />
            ) : null}
          </Box>
        </Pressable>
      </Modal>
    </Box>
  );
}

function ReasonRadio({
  code,
  bookingId,
  label,
  onSelect,
}: {
  code: OralExamIdRejectionReason;
  bookingId: string;
  label: string;
  onSelect: (code: OralExamIdRejectionReason) => void;
}) {
  return (
    <Pressable
      onPress={() => onSelect(code)}
      accessibilityRole="radio"
      testID={`id-reject-reason-code-${code}-${bookingId}`}
    >
      <Radio value={code} size="sm" alignItems="flex-start" pointerEvents="none">
        <HStack space="sm" alignItems="center" flex={1}>
          <RadioIndicator mr="$0" w="$3.5" h="$3.5" flexShrink={0}>
            <RadioIcon as={CircleIcon} size="xs" />
          </RadioIndicator>
          <RadioLabel size="sm" color="$textBody" mb="$0">
            {label}
          </RadioLabel>
        </HStack>
      </Radio>
    </Pressable>
  );
}

export function RetakeDialog({
  isOpen,
  bookingId,
  studentName,
  selfieUri,
  documentUri,
  selectedCode,
  errorMessage,
  pending,
  onClose,
  onSelectReason,
  onSubmit,
}: RetakeDialogProps) {
  const { t } = useTranslation();
  // The Gluestack modal draws through a portal that applies parent updates after
  // the keystroke. A note stored above this dialog makes React write the previous
  // text back, then the new text, and the caret lands at the end.
  const [note, setNote] = useState('');

  const handleClose = () => {
    if (pending) return;
    onClose();
  };

  const isOtherReason = selectedCode === OralExamIdRejectionReason.other;
  const noteRequired = isOtherReason && note.trim().length === 0;
  const submitDisabled = !selectedCode || pending || noteRequired;

  const reasonLabel = (code: OralExamIdRejectionReason) => {
    if (code === OralExamIdRejectionReason.other) {
      return t('oralExam.lecturer.idReview.rejectReasonOther', {
        defaultValue: 'Other (explain in the note)',
      });
    }
    return t(`oralExam.idReject.${code}`);
  };

  return (
    <GluestackModal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!pending}
    >
      <ModalBackdrop />
      <ModalContent
        maxWidth={560}
        softShadow="3"
        borderRadius="$xl"
        bg="$bgCard"
        testID={`id-reject-dialog-${bookingId}`}
      >
        <ModalHeader>
          <Heading size="md">
            {t('oralExam.lecturer.idReview.retakeDialogTitle', {
              defaultValue: 'Request a new photo from {{name}}',
              name: studentName,
            })}
          </Heading>
          <ModalCloseButton
            p="$2"
            m="-$2"
            disabled={pending}
            onPress={handleClose}
            testID={`id-reject-close-${bookingId}`}
            accessibilityLabel={t('common.buttons.close', { defaultValue: 'Close' })}
          >
            <Icon as={X} size="md" color="$textSubtle" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack space="md">
            <HStack space="md" alignItems="flex-start">
              <DialogPhoto
                uri={selfieUri}
                width={DIALOG_PORTRAIT_SIZE}
                height={DIALOG_PORTRAIT_SIZE}
                caption={t('oralExam.lecturer.idReview.captionPhoto', { defaultValue: 'Student photo' })}
                pressTestId={`id-review-selfie-photo-${bookingId}`}
                uriTestId={`id-review-selfie-uri-${bookingId}`}
                zoomTestId={`id-review-selfie-zoom-${bookingId}`}
                zoomAccessibilityLabel={`Zoom portrait for ${studentName}`}
                imageAccessibilityLabel={`Portrait for ${studentName}`}
              />
              <DialogPhoto
                uri={documentUri}
                width={DIALOG_DOCUMENT_WIDTH}
                height={DIALOG_DOCUMENT_HEIGHT}
                caption={t('oralExam.lecturer.idReview.captionIdDocument', { defaultValue: 'ID document' })}
                pressTestId={`id-review-photo-${bookingId}`}
                uriTestId={`id-review-photo-uri-${bookingId}`}
                zoomTestId={`id-review-photo-zoom-${bookingId}`}
                zoomAccessibilityLabel={`Zoom ID card for ${studentName}`}
                imageAccessibilityLabel={`ID card for ${studentName}`}
              />
            </HStack>

            <VStack space="sm">
              <Text size="sm" color="$textHeading" fontFamily="$heading">
                {t('oralExam.lecturer.idReview.rejectReasonPrompt', {
                  defaultValue: 'Reason for rejection',
                })}
              </Text>
              <RadioGroup
                value={selectedCode ?? ''}
                onChange={(value) => onSelectReason(value as OralExamIdRejectionReason)}
                accessibilityLabel={t('oralExam.lecturer.idReview.rejectReasonPrompt', {
                  defaultValue: 'Reason for rejection',
                })}
              >
                <HStack space="md" alignItems="flex-start">
                  <VStack flex={1} space="sm">
                    {REJECTION_REASON_LEFT.map((code) => (
                      <ReasonRadio
                        key={code}
                        code={code}
                        bookingId={bookingId}
                        label={reasonLabel(code)}
                        onSelect={onSelectReason}
                      />
                    ))}
                  </VStack>
                  <VStack flex={1} space="sm">
                    {REJECTION_REASON_RIGHT.map((code) => (
                      <ReasonRadio
                        key={code}
                        code={code}
                        bookingId={bookingId}
                        label={reasonLabel(code)}
                        onSelect={onSelectReason}
                      />
                    ))}
                  </VStack>
                </HStack>
              </RadioGroup>
            </VStack>

            <Textarea h={72} size="sm">
              <TextareaInput
                placeholder={
                  isOtherReason
                    ? t('oralExam.lecturer.idReview.rejectNoteRequiredPlaceholder', {
                      defaultValue: 'Explain what was wrong (required)',
                    })
                    : t('oralExam.lecturer.idReview.rejectNotePlaceholder', {
                      defaultValue: 'Additional note (optional)',
                    })
                }
                value={note}
                onChangeText={setNote}
                testID={`id-reject-note-${bookingId}`}
              />
            </Textarea>
            <Text size="xs" color="$textSubtle">
              {t('oralExam.lecturer.idReview.rejectNoteStudentVisibility', {
                defaultValue: 'The student sees this note in the lobby and in their result email.',
              })}
            </Text>
          </VStack>
        </ModalBody>
        {errorMessage ? (
          <Box px="$4" pb="$2">
            <Text
              size="sm"
              color="$statusError"
              testID={`id-reject-error-${bookingId}`}
            >
              {errorMessage}
            </Text>
          </Box>
        ) : null}
        <ModalFooter>
          <HStack space="sm" justifyContent="flex-end" width="$full">
            <Button
              size="md"
              variant="outline"
              action="secondary"
              onPress={handleClose}
              isDisabled={pending}
              testID={`id-reject-cancel-${bookingId}`}
            >
              <ButtonText>
                {t('oralExam.lecturer.idReview.rejectCancel', { defaultValue: 'Cancel' })}
              </ButtonText>
            </Button>
            <Button
              size="md"
              action="negative"
              onPress={() => onSubmit(note)}
              isDisabled={submitDisabled}
              testID={`id-reject-submit-${bookingId}`}
            >
              {pending ? <ButtonSpinner mr="$2" /> : null}
              <ButtonIcon as={RefreshCw} mr="$2" />
              <ButtonText>
                {t('oralExam.lecturer.idReview.rejectSubmit', { defaultValue: 'Request retake' })}
              </ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </GluestackModal>
  );
}
